/**
 * CollisionChip.tsx — Passive collision indicator chip (Phase 05-08, D-05-18).
 *
 * Displays a low-contrast ambient chip below the conversation header showing who is
 * currently viewing/working the conversation, so chatters can avoid duplicate replies.
 *
 * PASSIVE / NO HARD LOCK (D-05-18, T-05-30):
 *   - This chip is purely informational — it never blocks a chatter from opening/sending.
 *   - It updates live via the 'conversation:claimed' WS event.
 *   - When claimed_by_chatter_id === the current viewer's ID, the chip is hidden (no self-chip).
 *   - When claimed_by_chatter_id is null/undefined, the chip is hidden.
 *
 * WS event shape (conversation:claimed):
 *   { id: string, claimed_by_chatter_id: string | null, claimed_at: string | null }
 *
 * UI spec (05-UI-SPEC.md Surface 8):
 *   - Text: "Viewed by {chatterName}" or "Last replied by {chatterName} {N}m ago"
 *   - Style: text-xs text-text-subtle — deliberately low-contrast; ambient, not an alert
 *   - No border, no badge background — plain text only
 *   - aria-live="polite" for screen reader accessibility
 *   - Hidden when claimed_by_chatter_id is null or equals the current viewer's ID
 */

import * as React from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CollisionChipProps {
  /** The chatter ID who claimed (is viewing) the conversation. */
  claimedByChatterId: string | null | undefined;
  /** ISO timestamp when the claim was set. */
  claimedAt: string | null | undefined;
  /** The current viewer's chatter ID — chip is hidden if this matches claimedByChatterId. */
  currentChatterId: string | null | undefined;
  /**
   * Human-readable name of the claimer (resolved by parent from chatter list).
   * MUST be populated from ChatterDto.displayName (GET /chatters via chattersApi.list),
   * NOT from chatter email/UUID. SC-6 / D-09-16.
   */
  claimerName?: string | null;
  /** Whether the claimer last replied (vs just viewed). */
  lastReplied?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Return the number of minutes since a timestamp.
 * Returns null if the timestamp is invalid or too far in the past (>24h).
 */
function minutesAgo(isoString: string | null | undefined): number | null {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (isNaN(then)) return null;
  const diffMs = Date.now() - then;
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 0 || diffMin > 24 * 60) return null;
  return diffMin;
}

// ---------------------------------------------------------------------------
// CollisionChip
// ---------------------------------------------------------------------------

/**
 * CollisionChip — passive collision indicator.
 *
 * Renders a low-contrast ambient text chip below the conversation header.
 * Hidden when there is no active claimer or when the current viewer is the claimer.
 * Updates live via the 'conversation:claimed' WS event (parent passes updated props).
 *
 * @example
 * <CollisionChip
 *   claimedByChatterId={conv.claimed_by_chatter_id}
 *   claimedAt={conv.claimed_at}
 *   currentChatterId={currentUser.id}
 *   claimerName="Alice"
 *   lastReplied={conv.last_replier_chatter_id === conv.claimed_by_chatter_id}
 * />
 */
export function CollisionChip({
  claimedByChatterId,
  claimedAt,
  currentChatterId,
  claimerName,
  lastReplied = false,
}: CollisionChipProps) {
  // Hide when no claimer
  if (!claimedByChatterId) {
    return <div aria-live="polite" aria-atomic="true" />;
  }

  // Hide when the current viewer is the claimer (no self-chip)
  if (currentChatterId && claimedByChatterId === currentChatterId) {
    return <div aria-live="polite" aria-atomic="true" />;
  }

  // Build the display text
  const displayName = claimerName ?? 'another chatter';
  const minsAgo = minutesAgo(claimedAt);

  let chipText: string;
  if (lastReplied && minsAgo !== null) {
    chipText = `Last replied by ${displayName} ${minsAgo}m ago`;
  } else if (lastReplied) {
    chipText = `Last replied by ${displayName}`;
  } else {
    chipText = `Viewed by ${displayName}`;
  }

  return (
    // aria-live="polite" so screen readers announce changes (05-UI-SPEC.md Accessibility)
    <div
      aria-live="polite"
      aria-atomic="true"
      style={{
        // text-xs text-text-subtle — deliberately low-contrast (05-UI-SPEC.md Surface 8)
        fontSize: '12px',
        color: 'var(--color-text-subtle)',
        lineHeight: '1.4',
        // No border, no badge background — plain text only (Surface 8 spec)
        background: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
      }}
    >
      {chipText}
    </div>
  );
}
